/**
 * PropertyComputedCacheService
 *
 * Manages computed property cache for formulas and rollups.
 *
 * Features:
 * - Cache CRUD operations
 * - Batch invalidation
 * - Recalculation triggers
 * - Staleness detection
 * - Cache statistics and health monitoring
 *
 * Cache Strategy:
 * - Cache-first: Check cache before computing
 * - Automatic invalidation: Triggers mark cache as stale when dependencies change
 * - Lazy recalculation: Compute on read when stale
 * - Background batch: Optional batch recalculation of stale entries
 */

import { db } from '../db/database';
import { eventBus } from '../events/EventBus';
import { restSelect, restInsert, restUpdate, restDelete } from '../lib/supabaseRest';
import type { PropertyComputedCache } from '../types/advanced-properties';

// Edge Function URL
const EDGE_FUNCTION_URL = import.meta.env.VITE_SUPABASE_URL
  ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`
  : 'http://localhost:54321/functions/v1';

export class PropertyComputedCacheService {
  /**
   * Get cached value for a property on an object
   */
  async getCache(
    propertyId: string,
    objectId: string
  ): Promise<PropertyComputedCache | null> {
    try {
      const cache = await db.propertyComputedCache
        .where({ propertyId, objectId })
        .first();

      return cache || null;
    } catch (error) {
      console.error('[PropertyComputedCacheService] Error getting cache:', error);
      return null;
    }
  }

  /**
   * Set cached value
   */
  async setCache(
    propertyId: string,
    objectId: string,
    value: any
  ): Promise<void> {
    try {
      const cache: PropertyComputedCache = {
        id: this.generateId(),
        property_id: propertyId,
        object_id: objectId,
        computed_value: value,
        computed_at: new Date().toISOString(),
        is_stale: false,
      };

      // Upsert in Supabase (using onConflict for unique constraint)
      await restInsert<PropertyComputedCache>('property_computed_cache', [cache], {
        onConflict: 'property_id,object_id',
      });

      // Upsert locally
      const existing = await this.getCache(propertyId, objectId);
      if (existing) {
        await db.propertyComputedCache.update(existing.id, {
          computed_value: value,
          computed_at: cache.computed_at,
          is_stale: false,
        });
      } else {
        await db.propertyComputedCache.add(cache);
      }

      // Emit event
      eventBus.emit('cache:updated', {
        propertyId,
        objectId,
        value,
      });
    } catch (error) {
      console.error('[PropertyComputedCacheService] Error setting cache:', error);
      // Don't throw - caching is best-effort
    }
  }

  /**
   * Invalidate cache for a specific property/object (mark as stale)
   */
  async invalidateCache(propertyId: string, objectId: string): Promise<void> {
    try {
      // Mark as stale in Supabase
      await restUpdate(
        'property_computed_cache',
        { is_stale: true },
        {
          eq: {
            property_id: propertyId,
            object_id: objectId,
          },
        }
      );

      // Mark as stale locally
      const cache = await this.getCache(propertyId, objectId);
      if (cache) {
        await db.propertyComputedCache.update(cache.id, { is_stale: true });
      }

      // Emit event
      eventBus.emit('cache:invalidated', {
        propertyId,
        objectId,
      });
    } catch (error) {
      console.error('[PropertyComputedCacheService] Error invalidating cache:', error);
      // Don't throw - cache invalidation is best-effort
    }
  }

  /**
   * Invalidate all cache entries for a property across all objects
   */
  async invalidatePropertyCache(propertyId: string): Promise<void> {
    try {
      // Mark as stale in Supabase
      await restUpdate(
        'property_computed_cache',
        { is_stale: true },
        { eq: { property_id: propertyId } }
      );

      // Mark as stale locally
      const caches = await db.propertyComputedCache
        .where({ propertyId })
        .toArray();

      for (const cache of caches) {
        await db.propertyComputedCache.update(cache.id, { is_stale: true });
      }

      // Emit event
      eventBus.emit('cache:property-invalidated', { propertyId });
    } catch (error) {
      console.error('[PropertyComputedCacheService] Error invalidating property cache:', error);
      // Don't throw
    }
  }

  /**
   * Invalidate all cache entries for an object (all computed properties)
   */
  async invalidateObjectCache(objectId: string): Promise<void> {
    try {
      // Mark as stale in Supabase
      await restUpdate(
        'property_computed_cache',
        { is_stale: true },
        { eq: { object_id: objectId } }
      );

      // Mark as stale locally
      const caches = await db.propertyComputedCache
        .where({ objectId })
        .toArray();

      for (const cache of caches) {
        await db.propertyComputedCache.update(cache.id, { is_stale: true });
      }

      // Emit event
      eventBus.emit('cache:object-invalidated', { objectId });
    } catch (error) {
      console.error('[PropertyComputedCacheService] Error invalidating object cache:', error);
      // Don't throw
    }
  }

  /**
   * Get all stale cache entries
   */
  async getStaleEntries(): Promise<PropertyComputedCache[]> {
    try {
      const stale = await db.propertyComputedCache
        .where({ isStale: true })
        .toArray();

      return stale;
    } catch (error) {
      console.error('[PropertyComputedCacheService] Error getting stale entries:', error);
      return [];
    }
  }

  /**
   * Recalculate all stale entries using batch Edge Function
   */
  async recalculateStale(maxItems: number = 50): Promise<{
    computed: number;
    skipped: number;
    failed: number;
    duration: number;
  }> {
    try {
      const staleEntries = await this.getStaleEntries();

      if (staleEntries.length === 0) {
        return { computed: 0, skipped: 0, failed: 0, duration: 0 };
      }

      // Get unique object IDs (limit to maxItems)
      const objectIds = [...new Set(staleEntries.map(e => e.object_id))].slice(0, maxItems);

      // Call batch compute Edge Function
      const response = await fetch(`${EDGE_FUNCTION_URL}/batch-compute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`,
        },
        body: JSON.stringify({
          objectIds,
          maxItems,
        }),
      });

      if (!response.ok) {
        throw new Error(`Edge Function error: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Batch computation failed');
      }

      // Emit event
      eventBus.emit('cache:batch-recalculated', {
        computed: data.data.computed,
        duration: data.data.duration,
      });

      return {
        computed: data.data.computed || 0,
        skipped: data.data.skipped || 0,
        failed: data.data.failed || 0,
        duration: data.data.duration || 0,
      };
    } catch (error) {
      console.error('[PropertyComputedCacheService] Error recalculating stale entries:', error);
      return { computed: 0, skipped: 0, failed: 0, duration: 0 };
    }
  }

  /**
   * Clear cache entry
   */
  async clearCache(propertyId: string, objectId: string): Promise<void> {
    try {
      const cache = await this.getCache(propertyId, objectId);
      if (!cache) return;

      // Delete from Supabase
      await restDelete('property_computed_cache', {
        eq: {
          property_id: propertyId,
          object_id: objectId,
        },
      });

      // Delete locally
      await db.propertyComputedCache.delete(cache.id);

      // Emit event
      eventBus.emit('cache:cleared', { propertyId, objectId });
    } catch (error) {
      console.error('[PropertyComputedCacheService] Error clearing cache:', error);
      // Don't throw
    }
  }

  /**
   * Clear all cache entries for a property
   */
  async clearPropertyCache(propertyId: string): Promise<void> {
    try {
      // Delete from Supabase
      await restDelete('property_computed_cache', {
        eq: { property_id: propertyId },
      });

      // Delete locally
      const caches = await db.propertyComputedCache
        .where({ propertyId })
        .toArray();

      for (const cache of caches) {
        await db.propertyComputedCache.delete(cache.id);
      }

      // Emit event
      eventBus.emit('cache:property-cleared', { propertyId });
    } catch (error) {
      console.error('[PropertyComputedCacheService] Error clearing property cache:', error);
      // Don't throw
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{
    total: number;
    stale: number;
    fresh: number;
    stalePercentage: number;
  }> {
    try {
      const allCaches = await db.propertyComputedCache.toArray();
      const total = allCaches.length;
      const stale = allCaches.filter(c => c.is_stale).length;
      const fresh = total - stale;
      const stalePercentage = total > 0 ? (stale / total) * 100 : 0;

      return {
        total,
        stale,
        fresh,
        stalePercentage,
      };
    } catch (error) {
      console.error('[PropertyComputedCacheService] Error getting cache stats:', error);
      return {
        total: 0,
        stale: 0,
        fresh: 0,
        stalePercentage: 0,
      };
    }
  }

  /**
   * Get cache health status
   */
  async getCacheHealth(): Promise<{
    status: 'healthy' | 'degraded' | 'unhealthy';
    stalePercentage: number;
    recommendedAction?: string;
  }> {
    const stats = await this.getCacheStats();

    if (stats.stalePercentage < 20) {
      return {
        status: 'healthy',
        stalePercentage: stats.stalePercentage,
      };
    } else if (stats.stalePercentage < 50) {
      return {
        status: 'degraded',
        stalePercentage: stats.stalePercentage,
        recommendedAction: 'Consider running batch recalculation',
      };
    } else {
      return {
        status: 'unhealthy',
        stalePercentage: stats.stalePercentage,
        recommendedAction: 'Run batch recalculation immediately',
      };
    }
  }

  /**
   * Generate a unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const propertyComputedCacheService = new PropertyComputedCacheService();
